import { debounce } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'
import { LuCircleX, LuSearch, LuSearchX } from 'react-icons/lu'

import { useIntl } from 'react-intl'

import messages from './TableSearch.messages'
import styles from './TableSearch.module.css'

export type TableSearchProps = {
  debounceTime?: number
  defaultValue?: string | null
  inputName?: string
  onSearch: (term?: string) => void
  placeholder?: string
}

export const TableSearch = ({
  debounceTime = 300,
  defaultValue,
  inputName = 'table-search',
  onSearch,
  placeholder,
}: TableSearchProps) => {
  const { formatMessage } = useIntl()
  const [isOpen, setIsOpen] = useState(!!defaultValue)
  const [hasValue, setHasValue] = useState(!!defaultValue)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleInput = debounce(() => {
    const value = inputRef.current?.value ?? ''
    setHasValue(!!value)
    onSearch(value || undefined)
  }, debounceTime)

  const onTransitionEnd = () => {
    if (isOpen) {
      inputRef.current?.focus()
    }
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = defaultValue || ''
      setHasValue(!!defaultValue)
    }
  }, [defaultValue])

  const handleClear = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.focus()
    }
    setHasValue(false)
    onSearch(undefined)
  }

  const handleClose = () => {
    if (hasValue) {
      handleClear()
    }
    setIsOpen(false)
  }

  const handleTriggerClick = () => {
    if (!isOpen) {
      setIsOpen(true)
    } else if (hasValue) {
      handleClear()
    } else {
      handleClose()
    }
  }

  const TriggerIcon = !isOpen ? LuSearch : hasValue ? LuSearchX : LuCircleX

  return (
    <div className={styles.wrapper}>
      <div
        className={`${styles.fieldWrapper} ${isOpen ? styles.fieldWrapperOpen : ''}`}
        onTransitionEnd={onTransitionEnd}
      >
        <input
          className={styles.searchInput}
          name={inputName}
          onInput={handleInput}
          placeholder={placeholder || formatMessage(messages.defaultPlaceholder)}
          ref={inputRef}
          tabIndex={isOpen ? 0 : -1}
        />
      </div>
      <button
        type="button"
        className={styles.trigger}
        onClick={handleTriggerClick}
        data-testid="table-search-trigger"
      >
        <TriggerIcon color="#333" size={24} />
      </button>
    </div>
  )
}
